import { EventEmitter, Injectable } from '@angular/core';
import { RoutePath } from 'src/app/app-routing.module';
import { LocalizationService } from 'src/app/service/localization.service';
import { TvFeature, TvFeatureKey } from '../data/enum/tv-feature';
import { JsonKey } from '../data/type/locale-copy-key';

export interface EndScreenButton {
  isNext?: boolean;
  isChecked?: boolean;
  link?: string;
  copy: string;
  callback?: () => void;
  restart?: boolean;
  feature: TvFeature;
}

interface Feature {
  name: TvFeature;
  translationKey: string;
  routePath: RoutePath;
}
@Injectable({
  providedIn: 'root',
})
export class ProgressService {
  progressUpdated: EventEmitter<null> = new EventEmitter();
  comparisonButtonClick$: EventEmitter<boolean> = new EventEmitter();

  private readonly localStorageKey = 'visited-features';
  private hasLocalStorageAccess = false;
  private _hasDoneFirstNavigation = false;

  constructor() {
    this.initializeLocalStorage();
  }

  private features: Array<Feature> = [
    {
      name: TvFeature.BROWSE_TITLES,
      translationKey: JsonKey.END_SCREEN_LINK_1,
      routePath: RoutePath['browse-titles'],
    },
    {
      name: TvFeature.RECOMMENDATIONS,
      translationKey: JsonKey.END_SCREEN_LINK_2,
      routePath: RoutePath['recommendations'],
    },
    {
      name: TvFeature.WATCHLIST,
      translationKey: JsonKey.END_SCREEN_LINK_3,
      routePath: RoutePath['watchlist'],
    },
    {
      name: TvFeature.VOICE,
      translationKey: JsonKey.END_SCREEN_LINK_4,
      routePath: RoutePath['voice-search'],
    },
  ];

  private initializeLocalStorage() {
    const test = 'test';
    try {
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      this.hasLocalStorageAccess = true;
    } catch (e) {
      this.hasLocalStorageAccess = false;
    }

    if (this.hasLocalStorageAccess) {
      const savedVisitedFeatures = localStorage.getItem(this.localStorageKey);
      if (!savedVisitedFeatures) localStorage.setItem(this.localStorageKey, JSON.stringify([]));
    }
  }

  public setFeatureAsVisited(feature: TvFeatureKey) {
    if (this.hasLocalStorageAccess) {
      const visitedFeatures: Array<TvFeatureKey> = JSON.parse(
        localStorage.getItem(this.localStorageKey),
      );

      const hasFeatureBeenVisited = visitedFeatures.find((f) => f === feature);
      if (!hasFeatureBeenVisited)
        localStorage.setItem(this.localStorageKey, JSON.stringify([...visitedFeatures, feature]));
    }
    this.progressUpdated.emit();
  }

  private hasFeatureBeenVisited(feature: TvFeatureKey): boolean {
    if (!this.hasLocalStorageAccess) return false;

    const savedVisitedFeatures: Array<TvFeatureKey> = JSON.parse(
      localStorage.getItem(this.localStorageKey),
    );
    return savedVisitedFeatures.findIndex((visitedFeature) => visitedFeature === feature) >= 0;
  }

  public getEndScreenButtons(
    localizationService: LocalizationService,
    currentFeature: TvFeatureKey,
  ): Array<EndScreenButton> {
    return this.features.map((feature, index): EndScreenButton => {
      const currentFeatureIndex = this.features.findIndex(
        (someFeature) => someFeature.name === currentFeature,
      );

      return {
        isNext: index === currentFeatureIndex + 1,
        isChecked: this.hasFeatureBeenVisited(feature.name),
        link: localizationService.getLocalizedLink(feature.routePath),
        copy: localizationService.translate(JsonKey[feature.translationKey]),
        feature: feature.name,
      };
    });
  }

  public setHasDoneFirstNavigation() {
    this._hasDoneFirstNavigation = true;
  }

  public getHasDoneFirstNavigation() {
    return this._hasDoneFirstNavigation;
  }

  public setHasClickedComparisonButton() {
    this.comparisonButtonClick$.emit(true);
  }
}
